import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

interface AuthState {
  // Token is now stored in HttpOnly cookie, not in JS
  // We only track auth metadata here - isAuthenticated is derived from expiresAt
  expiresAt: string | null;
  walletAddress: string | null;
  isNewUser: boolean;
}

interface AuthActions {
  setAuth: (expiresAt: string, walletAddress: string, isNewUser?: boolean) => void;
  clearAuth: () => void;
  /** Returns true only if expiresAt exists and session is not expired */
  isAuthenticated: () => boolean;
  isSessionExpired: () => boolean;
}

interface AuthStore extends AuthState, AuthActions {}

const initialState: AuthState = {
  expiresAt: null,
  walletAddress: null,
  isNewUser: false,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setAuth: (expiresAt, walletAddress, isNewUser = false) => {
          set({
            expiresAt,
            walletAddress: walletAddress.toLowerCase(),
            isNewUser,
          });
        },

        clearAuth: () => {
          set(initialState);
        },

        /**
         * Returns true when the token should be refreshed.
         * Uses a 30-second buffer so refresh happens before actual expiry.
         */
        isSessionExpired: () => {
          const { expiresAt } = get();
          if (!expiresAt) return true;

          const expiryTime = new Date(expiresAt).getTime();
          if (Number.isNaN(expiryTime)) return true;
          // Refresh 30s before actual expiry
          return Date.now() >= expiryTime - 30_000;
        },

        /**
         * Returns true if the session is valid (not yet expired).
         * No buffer — reflects actual expiry so users aren't bounced early.
         * Use isSessionExpired() for proactive token refresh timing.
         */
        isAuthenticated: () => {
          const { expiresAt } = get();
          if (!expiresAt) return false;

          const expiryTime = new Date(expiresAt).getTime();
          if (Number.isNaN(expiryTime)) return false;
          return Date.now() < expiryTime;
        },
      }),
      {
        name: "hyperamm-auth",
        // Only persist auth metadata
        partialize: (state) => ({
          expiresAt: state.expiresAt,
          walletAddress: state.walletAddress,
          isNewUser: state.isNewUser,
        }),
      },
    ),
    {
      name: "hyperamm-auth-store",
    },
  ),
);
